import { useEffect, useState, type FormEvent } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  BackupCodesCountResponse,
  BackupCodesResponse,
  CompanyContactResponse,
  TwoFactorEnrollResponse,
  UpdateCompanyContactInput,
} from '@dsr/shared-types';
import { formatPhoneDisplay } from '@dsr/shared-types';
import { api, fetchMe, ApiError } from '../api/client';
import { useAuthStore, can } from '../auth/store';
import { toast } from '../components/Toasts';
import { PhoneInput } from '../components/PhoneInput';
import { Button, FormField, Input } from '../components/ui';

// Personal settings: password change + two-factor authentication.
//
// 2FA is OPTIONAL until the require_2fa system setting is flipped on the
// server — at which point AppShell routes un-enrolled users here until
// they complete enrollment. All endpoints already exist on the API; this
// page is pure UI over them.

function errMessage(err: unknown, fallback: string): string {
  if (err instanceof ApiError) return err.message || fallback;
  return fallback;
}

// ---------- Change password ----------

function ChangePasswordCard(): JSX.Element {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (next !== confirm) {
      toast.warn('New password and confirmation do not match.');
      return;
    }
    setBusy(true);
    try {
      await api.post('/api/auth/change-password', {
        current_password: current,
        new_password: next,
      });
      setCurrent('');
      setNext('');
      setConfirm('');
      toast.success('Password changed. Other sessions were signed out.');
    } catch (err) {
      toast.danger(errMessage(err, 'Password change failed.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="settings-card">
      <h2>Password</h2>
      <p className="settings-hint">
        Minimum 12 characters. Changing your password signs out every other
        device.
      </p>
      <form onSubmit={onSubmit} className="settings-form">
        <FormField label="Current password" required>
          <Input
            type="password"
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
            autoComplete="current-password"
            required
          />
        </FormField>
        <FormField label="New password" required>
          <Input
            type="password"
            value={next}
            onChange={(e) => setNext(e.target.value)}
            autoComplete="new-password"
            minLength={12}
            required
          />
        </FormField>
        <FormField label="Confirm new password" required>
          <Input
            type="password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            autoComplete="new-password"
            minLength={12}
            required
          />
        </FormField>
        <div className="settings-actions">
          <Button type="submit" variant="primary" disabled={busy}>
            {busy ? 'Saving…' : 'Change password'}
          </Button>
        </div>
      </form>
    </section>
  );
}

// ---------- Two-factor authentication ----------

// Sub-flows are mutually exclusive; one open panel at a time.
type TwoFaFlow = 'idle' | 'enrolling' | 'backup-codes' | 'disabling';

function TwoFactorCard(): JSX.Element {
  const me = useAuthStore((s) => s.me);
  const setMe = useAuthStore((s) => s.setMe);
  const queryClient = useQueryClient();
  const enabled = me?.user.is_2fa_enabled === true;

  const [flow, setFlow] = useState<TwoFaFlow>('idle');
  const [busy, setBusy] = useState(false);

  // Enrollment state
  const [enrollData, setEnrollData] = useState<TwoFactorEnrollResponse | null>(null);
  const [verifyCode, setVerifyCode] = useState('');

  // Shared password+code form (re-enroll, backup codes, disable)
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');

  // Freshly generated backup codes — shown exactly once.
  const [freshCodes, setFreshCodes] = useState<string[] | null>(null);

  const { data: codesCount } = useQuery({
    queryKey: ['2fa-backup-codes-count'],
    queryFn: () => api.get<BackupCodesCountResponse>('/api/auth/2fa/backup-codes-count'),
    enabled,
  });

  const refreshMe = async () => {
    const { user, ...rest } = await fetchMe();
    if (user) setMe({ user, ...rest });
    void queryClient.invalidateQueries({ queryKey: ['2fa-backup-codes-count'] });
  };

  const resetFlow = () => {
    setFlow('idle');
    setEnrollData(null);
    setVerifyCode('');
    setPassword('');
    setCode('');
  };

  const startEnroll = async () => {
    setBusy(true);
    try {
      // Re-enrollment over an active 2FA requires password + current code;
      // first-time enrollment posts an empty body.
      const body = enabled ? { current_password: password, code } : {};
      const data = await api.post<TwoFactorEnrollResponse>('/api/auth/2fa/enroll', body);
      setEnrollData(data);
      setPassword('');
      setCode('');
    } catch (err) {
      toast.danger(errMessage(err, 'Could not start 2FA enrollment.'));
    } finally {
      setBusy(false);
    }
  };

  const verifyEnroll = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post('/api/auth/2fa/verify', { code: verifyCode });
      toast.success('Two-factor authentication is on. Generate backup codes next.');
      resetFlow();
      await refreshMe();
      setFlow('backup-codes');
    } catch (err) {
      toast.danger(errMessage(err, 'That code was not accepted.'));
    } finally {
      setBusy(false);
    }
  };

  const generateBackupCodes = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      const data = await api.post<BackupCodesResponse>('/api/auth/2fa/backup-codes', {
        current_password: password,
        code,
      });
      setFreshCodes(data.codes);
      resetFlow();
      void queryClient.invalidateQueries({ queryKey: ['2fa-backup-codes-count'] });
    } catch (err) {
      toast.danger(errMessage(err, 'Could not generate backup codes.'));
    } finally {
      setBusy(false);
    }
  };

  const disable2fa = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post('/api/auth/2fa/disable', { current_password: password, code });
      toast.success('Two-factor authentication is off.');
      setFreshCodes(null);
      resetFlow();
      await refreshMe();
    } catch (err) {
      toast.danger(errMessage(err, 'Could not disable 2FA.'));
    } finally {
      setBusy(false);
    }
  };

  const copyCodes = async () => {
    if (!freshCodes) return;
    try {
      await navigator.clipboard.writeText(freshCodes.join('\n'));
      toast.success('Backup codes copied to clipboard.');
    } catch {
      toast.warn('Copy failed — select and copy the codes manually.');
    }
  };

  // Reusable password + 6-digit-code field pair for the verified actions.
  const credentialFields = (
    <>
      <FormField label="Current password" required>
        <Input
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoComplete="current-password"
          required
        />
      </FormField>
      <FormField label="Authenticator code" required>
        <Input
          type="text"
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
          inputMode="numeric"
          autoComplete="one-time-code"
          placeholder="123456"
          required
        />
      </FormField>
    </>
  );

  return (
    <section className="settings-card">
      <div className="settings-card-head">
        <h2>Two-factor authentication</h2>
        <span className={`settings-badge ${enabled ? 'on' : 'off'}`}>
          {enabled ? 'Enabled' : 'Off'}
        </span>
      </div>
      <p className="settings-hint">
        Adds a 6-digit authenticator code to your sign-in. Backup codes let
        you in if you lose the device{enabled && codesCount ? (
          <> — <strong>{codesCount.remaining}</strong> unused backup
          code{codesCount.remaining === 1 ? '' : 's'} remaining</>
        ) : null}.
      </p>

      {/* One-time display of freshly generated backup codes. */}
      {freshCodes && (
        <div className="settings-codes" role="status">
          <p className="settings-codes-note">
            Store these somewhere safe — they are shown only once. Each code
            works one time.
          </p>
          <ul className="settings-codes-list">
            {freshCodes.map((c) => (
              <li key={c}><code>{c}</code></li>
            ))}
          </ul>
          <div className="settings-actions">
            <button type="button" className="btn" onClick={copyCodes}>
              Copy all
            </button>
            <Button variant="ghost" onClick={() => setFreshCodes(null)}>
              I saved them
            </Button>
          </div>
        </div>
      )}

      {flow === 'idle' && (
        <div className="settings-actions">
          {!enabled && (
            <Button
              variant="primary"
              disabled={busy}
              onClick={() => {
                setFlow('enrolling');
                void startEnroll();
              }}
            >
              Set up two-factor
            </Button>
          )}
          {enabled && (
            <>
              <button type="button" className="btn" onClick={() => setFlow('backup-codes')}>
                Regenerate backup codes
              </button>
              <Button variant="danger" onClick={() => setFlow('disabling')}>
                Disable 2FA
              </Button>
            </>
          )}
        </div>
      )}

      {flow === 'enrolling' && (
        <div className="settings-flow">
          {!enrollData ? (
            <p className="settings-hint">Preparing enrollment…</p>
          ) : (
            <form onSubmit={verifyEnroll} className="settings-form">
              <p className="settings-hint">
                Scan the QR code with your authenticator app (or enter the
                secret manually), then confirm with a code.
              </p>
              <img
                className="settings-qr"
                src={enrollData.qr_code_data_url}
                alt="2FA enrollment QR code"
                width={180}
                height={180}
              />
              <p className="settings-secret">
                Secret: <code>{enrollData.secret_base32}</code>
              </p>
              <FormField label="6-digit code from the app" required>
                <Input
                  type="text"
                  value={verifyCode}
                  onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  placeholder="123456"
                  required
                />
              </FormField>
              <div className="settings-actions">
                <Button type="submit" variant="primary" disabled={busy || verifyCode.length !== 6}>
                  {busy ? 'Verifying…' : 'Turn on 2FA'}
                </Button>
                <Button variant="ghost" onClick={resetFlow}>
                  Cancel
                </Button>
              </div>
            </form>
          )}
        </div>
      )}

      {flow === 'backup-codes' && (
        <form onSubmit={generateBackupCodes} className="settings-form settings-flow">
          <p className="settings-hint">
            Generating a new batch invalidates all previous backup codes.
            Confirm with your password and a current authenticator code.
          </p>
          {credentialFields}
          <div className="settings-actions">
            <Button type="submit" variant="primary" disabled={busy}>
              {busy ? 'Generating…' : 'Generate 10 codes'}
            </Button>
            <Button variant="ghost" onClick={resetFlow}>
              Cancel
            </Button>
          </div>
        </form>
      )}

      {flow === 'disabling' && (
        <form onSubmit={disable2fa} className="settings-form settings-flow">
          <p className="settings-hint">
            Turning 2FA off removes the authenticator requirement and wipes
            your backup codes.
          </p>
          {credentialFields}
          <div className="settings-actions">
            <Button type="submit" variant="danger" disabled={busy}>
              {busy ? 'Disabling…' : 'Disable 2FA'}
            </Button>
            <Button variant="ghost" onClick={resetFlow}>
              Cancel
            </Button>
          </div>
        </form>
      )}
    </section>
  );
}

// ---------- Company contact (settings.write_company) ----------
//
// Edits the system_settings company block. The marketing site reads the
// same data from GET /api/public/company for its footer / contact page.

function CompanyContactCard(): JSX.Element {
  const queryClient = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [address, setAddress] = useState('');
  const [website, setWebsite] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['settings-company'],
    queryFn: () => api.get<CompanyContactResponse>('/api/settings/company'),
  });

  // Hydrate the form once the current values arrive (and after saves).
  useEffect(() => {
    if (!data) return;
    setName(data.company.name);
    setEmail(data.company.email ?? '');
    setPhone(formatPhoneDisplay(data.company.phone));
    setAddress(data.company.address ?? '');
    setWebsite(data.company.website ?? '');
  }, [data]);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body: UpdateCompanyContactInput = {
        name: name.trim(),
        email: email.trim() || null,
        phone: phone.trim() || null,
        address: address.trim() || null,
        website: website.trim() || null,
      };
      await api.patch<CompanyContactResponse>('/api/settings/company', body);
      void queryClient.invalidateQueries({ queryKey: ['settings-company'] });
      toast.success('Company contact info saved. The public site reflects it immediately.');
    } catch (err) {
      toast.danger(errMessage(err, 'Could not save company contact info.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="settings-card">
      <h2>Company contact</h2>
      <p className="settings-hint">
        Shown on the public marketing site (footer and contact page).
        Leave a field blank to hide it.
      </p>
      {isLoading ? (
        <p className="settings-hint">Loading…</p>
      ) : (
        <form onSubmit={onSubmit} className="settings-form">
          <FormField label="Company name" required>
            <Input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={200}
              required
            />
          </FormField>
          <FormField label="Public email">
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="contact@darkspirerisk.com"
              maxLength={320}
            />
          </FormField>
          <FormField label="Public phone">
            <PhoneInput
              value={phone}
              onChange={setPhone}
              placeholder="(555) 555-0100"
              maxLength={40}
            />
          </FormField>
          <FormField label="Address">
            <Input
              type="text"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              maxLength={500}
            />
          </FormField>
          <FormField label="Website">
            <Input
              type="url"
              value={website}
              onChange={(e) => setWebsite(e.target.value)}
              placeholder="https://www.darkspirerisk.com"
              maxLength={320}
            />
          </FormField>
          <div className="settings-actions">
            <Button type="submit" variant="primary" disabled={busy}>
              {busy ? 'Saving…' : 'Save company info'}
            </Button>
          </div>
        </form>
      )}
    </section>
  );
}

export function SettingsPage(): JSX.Element {
  const me = useAuthStore((s) => s.me);
  const mustEnroll = me?.requires_2fa_enrollment === true;

  return (
    <div className="settings-page">
      <header className="page-header">
        <div>
          <h1>Settings</h1>
          <p className="page-sub">
            {me?.user.display_name ?? me?.user.email} · personal security
          </p>
        </div>
      </header>

      {mustEnroll && (
        <div className="settings-mandate" role="alert">
          Your organization requires two-factor authentication. Set it up
          below to continue using the console.
        </div>
      )}

      <div className="settings-grid">
        <TwoFactorCard />
        <ChangePasswordCard />
        {can('settings.write_company') && <CompanyContactCard />}
      </div>
    </div>
  );
}
